import {
  EJustification,
  ESectionInfoType,
  EVariableType,
  SUBMODEL_PREFIX,
} from '../data/Constants';
import {
  IAssignmentItem,
  IConfigurationValue,
  IConfigurationVariable,
  IExtendedConfigureResponse,
  INumericFallbackEntry,
  IProductAssignments,
  ISection,
  ISectionAssignments,
} from '../../types';
import {
  getViewIdSuffix,
  getMultiNumericCode,
  getFlatSections,
  isLessFeature,
  getSubModelsFromSectionId,
  isSubModelAssigned,
} from './ConfigurationDataHelperFunctions';
import { isScope } from './SummaryHelperFunction';
import { getVariableSalesText } from './SalesTextHelperFunctions';

/**
 * Helper function to extract product ID from section ID by finding parts that end with viewIdSuffix
 * @description Parses section ID to find product identifiers by splitting on dots and filtering
 * for parts ending with the view ID suffix, then removes the suffix to get clean product ID
 * @param {string} sectionId - The complete section identifier (e.g., "productId.sectionName_CPQ")
 * @param {string} viewIdSuffix - The view identifier suffix to match against (e.g., "_CPQ")
 * @returns {string | undefined} The extracted product ID without suffix, or undefined if not found
 * @example
 * extractProductId("123.456_CPQ.789", "_CPQ") // returns "456"
 * extractProductId("123.456.789", "_CPQ") // returns undefined
 */
const extractProductId = (sectionId: string, viewIdSuffix: string): string | undefined => {
  return sectionId
    .split('.')
    .filter((sIdPart) => sIdPart.endsWith(viewIdSuffix))
    .map((sIdPart) => sIdPart.replace(viewIdSuffix, ''))
    .pop();
};

/**
 * Filters configuration variable values by a specific justification type
 * @param {IConfigurationVariable} variable - The configuration variable containing values to filter
 * @param {IConfigurationValue['assigned']} justification - The assignment justification to match (e.g. 'ByRule', 'ByDefault')
 * @returns {IConfigurationValue[]} Array of values matching the given justification
 */
function getValuesByJustification(
  variable: IConfigurationVariable,
  justification: IConfigurationValue['assigned']
): IConfigurationValue[] {
  return variable.values.filter((v) => {
    const isMatch = v.assigned === justification;
    const isLessAndDefault =
      justification === EJustification.Default &&
      isLessFeature(String(v.value)) &&
      Boolean(v.assigned);

    if (variable.variableType === EVariableType.Number) {
      return isMatch && Number(v.value) > 0;
    } else if (variable.variableType === EVariableType.String) {
      return isMatch && String(v.value).trim() !== '';
    }

    return isMatch || isLessAndDefault;
  });
}

/**
 * Processes Number/String variable types and returns the resulting assignment items
 * @param {IConfigurationVariable} variable - The variable to process
 * @param {string} variableId - The extracted variable ID
 * @param {IConfigurationValue[]} assignedValues - Values assigned by rule
 * @param {ISection} section - The section containing the variable (to check if multi-numeric)
 * @param {string} [fallbackValue] - Optional numeric fallback value
 * @returns {IAssignmentItem[]} New items for this variable
 */
function processNumericOrStringAssignments(
  variable: IConfigurationVariable,
  variableId: string,
  assignedValues: IConfigurationValue[],
  section: ISection,
  fallbackValue?: string
): IAssignmentItem[] {
  const result: IAssignmentItem[] = [];
  let featureId = getVariableId(variableId);

  assignedValues.forEach((ruleValue) => {
    const displayValue = ruleValue.value ?? '';
    let optionId = `${featureId} (${displayValue})`;
    const name = variable.shortSalesText ?? variable.name ?? '';

    if (section.type === ESectionInfoType.MultiNumeric) {
      featureId = getMultiNumericCode(section.id);
    } else if (variable.variableType === EVariableType.String) {
      optionId = String(displayValue);
    }

    const featureExist = result.some((s) => s.featureId === featureId);
    result.push({
      featureId: featureExist ? getHierarchicalVariableId(featureId) : featureId,
      optionId,
      name,
    });
  });

  // Numeric fallback: the range minimum was raised by a rule but Configit doesn't mark it ByRule,
  // so it isn't in assignedValues — add it explicitly so it appears in the AutoSelection dialog.
  if (fallbackValue !== undefined && variable.variableType === EVariableType.Number) {
    const fallbackOptionId = `${featureId} (${fallbackValue})`;
    if (section.type === ESectionInfoType.MultiNumeric) {
      featureId = getMultiNumericCode(section.id);
    }
    const featureExist = result.some((s) => s.featureId === featureId);
    result.push({
      featureId: featureExist ? getHierarchicalVariableId(featureId) : featureId,
      optionId: fallbackOptionId,
      name: variable.shortSalesText ?? variable.name ?? '',
    });
  }

  return result;
}

/**
 * Processes Singleselect/Multiselect variable types and returns the resulting assignment items
 * @param {string} variableId - The extracted variable ID
 * @param {IConfigurationValue[]} assignedValues - Values assigned by rule
 * @returns {IAssignmentItem[]} New items for this variable
 */
function processSingleOrMultiSelectAssignments(
  variableId: string,
  assignedValues: IConfigurationValue[]
): IAssignmentItem[] {
  const result: IAssignmentItem[] = [];
  assignedValues.forEach((ruleValue) => {
    if (ruleValue.name && ruleValue.value !== undefined) {
      const valueStr = String(ruleValue.value);
      const name = ruleValue.shortSalesText ?? ruleValue.name;
      const optionExist = result.some((s) => s.optionId === valueStr);
      result.push({
        featureId: optionExist ? getHierarchicalVariableId(variableId) : getVariableId(variableId),
        optionId: valueStr,
        name,
      });
    }
  });
  return result;
}

/**
 * Processes a configuration variable and returns its assignments for the given justification
 * @param {IConfigurationVariable} variable - The variable to process
 * @param {string} productKey - The product key
 * @param {ISection[]} sections - All sections for parent name lookup
 * @param {ISection} section - The section containing the variable
 * @param {IConfigurationValue['assigned']} justification - The assignment justification to filter by
 * @returns {IProductAssignments | null} Assignments for this variable, or null if none match
 */
function processAssignmentsByJustification(
  variable: IConfigurationVariable,
  productKey: string,
  sections: ISection[],
  section: ISection,
  justification: IConfigurationValue['assigned'],
  fallbackValue?: string
): IProductAssignments | null {
  const matchedValues = getValuesByJustification(variable, justification);
  if (matchedValues.length === 0 && fallbackValue === undefined) return null;

  const sectionName =
    section.type === ESectionInfoType.MultiNumeric
      ? resolveSectionName(section, sections)
      : (section.name ?? '## Default ##');

  const varType = variable.variableType;
  let sectionArray: IAssignmentItem[] = [];
  if (varType === EVariableType.Number || varType === EVariableType.String) {
    sectionArray = processNumericOrStringAssignments(
      variable,
      variable.id,
      matchedValues,
      section,
      fallbackValue
    );
  } else if (varType === EVariableType.Singleselect || varType === EVariableType.Multiselect) {
    sectionArray = processSingleOrMultiSelectAssignments(variable.id, matchedValues);
  }

  if (sectionArray.length === 0) return null;
  return { [productKey]: { [sectionName]: sectionArray } };
}

/**
 * Resolves the product name from configuration model context
 * @param {string} productId - The product ID to look up
 * @param {IExtendedConfigureResponse} configuration - The configuration state
 * @returns {string} The product name or productId as fallback
 */
function getProductName(productId: string, configuration: IExtendedConfigureResponse): string {
  const subModel = configuration?.savedConfiguration?.modelContext?.subModels?.find(
    (sm) => sm.id === productId
  );
  let name = subModel
    ? subModel.name
    : configuration?.savedConfiguration?.modelContext?.rootModel?.name;
  const id = subModel
    ? subModel.id
    : configuration?.savedConfiguration?.modelContext?.rootModel?.id;
  name = getVariableSalesText(configuration.shortSalesText, id) ?? name;

  return name ?? productId;
}

/**
 * Resolves the section name, using parent section name for multiNumeric types
 * @description For multi-numeric sections, returns the parent section's name instead of the child's name.
 * This ensures proper grouping of multi-numeric variables under their parent section heading.
 * @param {ISection} section - The section to resolve the name for
 * @param {ISection[]} allSections - All sections for parent lookup
 * @returns {string} The resolved section name (parent name for multi-numeric, or section name otherwise)
 * @example
 * // Given a multi-numeric section with hierarchical ID:
 * // section.id = "722223_CPQ.722350_CPQ.S722350_53.S722350_FCV0242_999"
 * // section.name = "FCV0894"
 * // Parent section (id: "95211_CPQ", name: "Configuration Options")
 * resolveSectionName(section, allSections)
 * // Returns: "Configuration Options" (parent section name)
 *
 * @example
 * // Given a regular section without parent:
 * // section.id = "95211_CPQ"
 * // section.name = "Main Settings"
 * resolveSectionName(section, allSections)
 * // Returns: "Main Settings" (own section name)
 */
function resolveSectionName(section: ISection, allSections: ISection[]): string {
  // Use section's own name as fallback, or a default if name is undefined
  const defaultName = section.name ?? '## Multi Numeric Default ##';

  // Split section ID by dots to determine hierarchy level
  // Example: "722223_CPQ.722350_CPQ.S722350_53.S722350_FCV0242_999" becomes ["722223_CPQ", "722350_CPQ", "S722350_53", "S722350_FCV0242_999"]
  const idParts = section.id.split('.');

  // If only one part exists, this is a root section with no parent
  if (idParts.length <= 1) {
    return defaultName;
  }

  // Reconstruct parent ID by removing the last segment
  // Example: ["722223_CPQ", "722350_CPQ", "S722350_53", "S722350_FCV0242_999"] becomes "722223_CPQ.722350_CPQ.S722350_53"
  const parentId = idParts.slice(0, -1).join('.');

  // Find parent section in the full sections array by matching IDs
  const parentSection = allSections.find((sec) => sec.id === parentId);

  // Return parent's name if found, otherwise fall back to section's own name
  return parentSection?.name ?? defaultName;
}

/**
 * Checks if a section should be processed for rule/default assignments
 * @description Excludes scope sections and sections marked as unavailable. For sections nested
 * under a submodel, when checking Default justification, only processes the section if the
 * submodel itself is assigned.
 * @param {ISection} section - The section to check
 * @param {IExtendedConfigureResponse} configuration - The configuration state, used to verify submodel assignment
 * @param {string} justification - The assignment justification currently being processed (e.g. 'ByRule', 'ByDefault')
 * @returns {boolean} True if section should be processed
 */
function shouldProcessSection(
  section: ISection,
  configuration: IExtendedConfigureResponse,
  justification: string
): boolean {
  let isValidSection = true;
  const submodels = getSubModelsFromSectionId(section.id);
  if (submodels.length > 0 && justification === EJustification.Default) {
    isValidSection = isSubModelAssigned(configuration, submodels);
  }
  return !section.isItemsAvailable && !isScope(section) && isValidSection;
}

/**
 * Builds an assignments collection for all sections, filtering values by a given justification.
 * Optionally includes numeric fallback variables (range-minimum auto-assigns not marked ByRule by Configit)
 * so they appear alongside rule-assigned items in the AutoSelection dialog.
 * @param {ISection[]} sections - Flat sections to iterate
 * @param {IExtendedConfigureResponse} configuration - Configuration state for product name lookup
 * @param {IConfigurationValue['assigned']} justification - The assignment justification to filter by
 * @param {Record<string, string>} fallbacks - Optional map of variableId → fallback value to include
 * @returns {IProductAssignments} Nested map of product → section → assignment items
 */
export function buildAssignmentsByJustification(
  sections: ISection[],
  configuration: IExtendedConfigureResponse,
  justification: IConfigurationValue['assigned'],
  fallbacks: Record<string, INumericFallbackEntry> = {}
): IProductAssignments {
  const assignments: IProductAssignments = {};
  const viewIdSuffix = getViewIdSuffix();
  const fallbackIds = new Set(Object.keys(fallbacks));

  sections.forEach((section: ISection) => {
    const productId = extractProductId(section.id, viewIdSuffix);
    if (!productId) return;

    const productName = getProductName(productId, configuration);
    const productKey = `${productId} (${productName})`;

    if (shouldProcessSection(section, configuration, justification)) {
      section.variables.forEach((variable) => {
        if (variable.variableType === EVariableType.Submodel) {
          return;
        }
        const fallbackEntry = fallbacks[variable.id];
        const fallbackValue =
          justification === EJustification.Rule &&
          fallbackIds.has(variable.id) &&
          !fallbackEntry.isUserDeselected
            ? fallbackEntry.value
            : undefined;
        const result = processAssignmentsByJustification(
          variable,
          productKey,
          sections,
          section,
          justification,
          fallbackValue
        );
        if (!result) return;
        for (const [pk, secs] of Object.entries(result)) {
          assignments[pk] ??= {};
          for (const [sn, items] of Object.entries(secs)) {
            assignments[pk][sn] ??= [];
            assignments[pk][sn].push(...items);
          }
        }
      });
    }
  });

  return assignments;
}

/**
 * Compares two arrays and returns items that exist in current but not in previous
 * @param {IAssignmentItem[]} currentArray - Current array
 * @param {IAssignmentItem[]} previousArray - Previous array
 * @returns {IAssignmentItem[]} Array of newly added items
 */
function getArrayDifferences(
  currentArray: IAssignmentItem[],
  previousArray: IAssignmentItem[]
): IAssignmentItem[] {
  // Quick check if arrays are identical
  if (JSON.stringify(currentArray) === JSON.stringify(previousArray)) {
    return [];
  }

  // Find items in current that don't exist in previous
  const previousSet = new Set(previousArray.map((item) => JSON.stringify(item)));
  return currentArray.filter((item) => !previousSet.has(JSON.stringify(item)));
}

/**
 * Processes a single section comparison and updates differences
 * @param {IAssignmentItem[]} currentArray - Current section assignments
 * @param {IAssignmentItem[]} previousArray - Previous section assignments
 * @param {string} productKey - Product key for error logging
 * @param {string} sectionName - Section name for error logging
 * @returns {{ items: IAssignmentItem[]; count: number }} Added items and their count
 */
function processSectionDifferences(
  currentArray: IAssignmentItem[],
  previousArray: IAssignmentItem[],
  productKey: string,
  sectionName: string
): { items: IAssignmentItem[]; count: number } {
  try {
    const addedItems = getArrayDifferences(currentArray, previousArray);
    return { items: addedItems, count: addedItems.length };
  } catch (error) {
    console.error(`Error comparing arrays for ${productKey} > ${sectionName}:`, error);
    // Fallback: treat entire current array as new
    return { items: currentArray, count: currentArray.length };
  }
}

/**
 * Adds section differences to the result if there are any changes
 * @param {IProductAssignments} differences - The differences accumulator
 * @param {string} productKey - The product key
 * @param {string} sectionName - The section name
 * @param {IAssignmentItem[]} items - The items to add
 * @returns {void}
 */
function addSectionDifferences(
  differences: IProductAssignments,
  productKey: string,
  sectionName: string,
  items: IAssignmentItem[]
): void {
  if (items.length > 0) {
    if (!differences[productKey]) {
      differences[productKey] = {};
    }
    differences[productKey][sectionName] = items;
  }
}

/**
 * Processes all sections for a given product and returns differences
 * @param {ISectionAssignments} currentSections - Current sections for the product
 * @param {ISectionAssignments} previousSections - Previous sections for the product
 * @param {string} productKey - The product key
 * @param {IProductAssignments} differences - The differences accumulator
 * @returns {number} Total count of differences found
 */
function processProductSections(
  currentSections: ISectionAssignments,
  previousSections: ISectionAssignments,
  productKey: string,
  differences: IProductAssignments
): number {
  let count = 0;

  for (const sectionName of Object.keys(currentSections)) {
    const currentArray = Array.isArray(currentSections[sectionName])
      ? currentSections[sectionName]
      : [];
    const previousArray = Array.isArray(previousSections[sectionName])
      ? previousSections[sectionName]
      : [];

    const { items, count: sectionCount } = processSectionDifferences(
      currentArray,
      previousArray,
      productKey,
      sectionName
    );

    addSectionDifferences(differences, productKey, sectionName, items);
    count += sectionCount;
  }

  return count;
}

/**
 * Compare two currentAssignmentsByRule objects and return differences with count
 * @description Only tracks newly added items (current state changes), not removed items.
 * Handles nested structure: { "productId#productName": { "sectionName": [{ id, name, featureid }] } }
 * @param {IProductAssignments} previous - Previous currentAssignmentsByRule state
 * @param {IProductAssignments} current - Current currentAssignmentsByRule from getSelectableSections
 * @returns {{ differences: IProductAssignments; count: number }} - Object containing differences (added items only) and count
 */
export const getDeltaAssignmentsByRule = (
  previous: IProductAssignments,
  current: IProductAssignments
): { differences: IProductAssignments; count: number } => {
  const differences: IProductAssignments = {};
  const safePrevious = previous ?? {};
  const safeCurrent = current ?? {};
  let totalCount = 0;

  for (const productKey of Object.keys(safeCurrent)) {
    const currentSections = safeCurrent[productKey];

    if (!currentSections || typeof currentSections !== 'object') continue;

    const previousSections = safePrevious[productKey] ?? {};
    totalCount += processProductSections(
      currentSections,
      previousSections,
      productKey,
      differences
    );
  }

  return { differences, count: totalCount };
};

/**
 * Counts the total number of assignment items across all products and sections
 * @param {IProductAssignments} assignments - The assignments object to count
 * @returns {number} Total count of assignment items
 */
export const countAssignmentItems = (assignments: IProductAssignments): number => {
  return Object.values(assignments).reduce(
    (total, sections) =>
      total + Object.values(sections).reduce((sTotal, items) => sTotal + items.length, 0),
    0
  );
};

/**
 * Removes the submodel prefix from a variable ID to get the hierarchical identifier
 * @description Strips the 'I_' prefix from submodel variable IDs to create a clean hierarchical path
 * @param {string} variableId - The variable ID that may contain the submodel prefix
 * @returns {string} The variable ID with the submodel prefix removed
 * @example
 * getHierarchicalVariableId("I_722467.I_867030.APPLICATION_AREAS") // returns "722467.867030.APPLICATION_AREAS"
 * getHierarchicalVariableId("RegularVar") // returns "RegularVar"
 */
function getHierarchicalVariableId(variableId: string): string {
  return variableId.replaceAll(SUBMODEL_PREFIX, '');
}

/**
 * Extracts the last segment from a dot-separated variable ID
 * @description Splits the variable ID by dots and returns the final part, removing any hierarchical path prefixes
 * @param {string} variableId - The full variable ID with potential dot-separated path (e.g., "I_SubModel_1.Feature.Variable")
 * @returns {string} The last segment of the variable ID without the hierarchical path
 * @example
 * getVariableId("I_722467.I_867030.APPLICATION_AREAS") // returns "APPLICATION_AREAS"
 * getVariableId("SimpleVariable") // returns "SimpleVariable"
 */
function getVariableId(variableId: string): string {
  return variableId.split('.').pop()!;
}

/**
 * Checks if any default assignment exists in the configuration (optimized early-exit)
 * @description This function is optimized to stop as soon as the first default assignment is found.
 * Use this for determining whether to show the information dialog, where we only need to know
 * if at least one default exists, not the complete list.
 * @param {ISection[]} flatSections - Flat array of configuration sections
 * @param {IExtendedConfigureResponse} configuration - The configuration state, used to verify submodel assignment
 * @returns {boolean} True if at least one default assignment exists
 */
export const hasDefaultAssignments = (
  flatSections: ISection[],
  configuration: IExtendedConfigureResponse
): boolean => {
  for (const section of flatSections) {
    if (shouldProcessSection(section, configuration, EJustification.Default)) {
      for (const variable of section.variables) {
        if (hasDefaultValue(variable)) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Splits a flat assignments map into regular and _LESS buckets.
 * _LESS items are Singleselect values that became incompatible and must be shown separately.
 * @param {IProductAssignments} allAssignments - Combined assignments from buildAssignmentsByJustification
 * @returns {{ regularAssignments: IProductAssignments; lessAssignments: IProductAssignments }}
 */
function segregateLessAssignments(allAssignments: IProductAssignments): {
  regularAssignments: IProductAssignments;
  lessAssignments: IProductAssignments;
} {
  const regularAssignments: IProductAssignments = {};
  const lessAssignments: IProductAssignments = {};

  for (const [productKey, sections] of Object.entries(allAssignments)) {
    for (const [sectionName, items] of Object.entries(sections)) {
      const lessItems = items.filter((item) => isLessFeature(item.optionId));
      const regularItems = items.filter((item) => !isLessFeature(item.optionId));
      if (lessItems.length > 0) {
        lessAssignments[productKey] ??= {};
        lessAssignments[productKey][sectionName] = lessItems;
      }
      if (regularItems.length > 0) {
        regularAssignments[productKey] ??= {};
        regularAssignments[productKey][sectionName] = regularItems;
      }
    }
  }

  return { regularAssignments, lessAssignments };
}

/**
 * Computes default assignments split into regular and _LESS buckets
 * @param {IExtendedConfigureResponse} configuration - The configuration state
 * @returns {{ assignments: IProductAssignments; lessAssignments: IProductAssignments }}
 */
export const computeDefaultAssignments = (
  configuration: IExtendedConfigureResponse
): {
  assignments: IProductAssignments;
  lessAssignments: IProductAssignments;
} => {
  const flatSections = getFlatSections(configuration);
  if (flatSections.length === 0) return { assignments: {}, lessAssignments: {} };

  const allAssignments = buildAssignmentsByJustification(
    flatSections,
    configuration,
    EJustification.Default
  );

  const { regularAssignments, lessAssignments } = segregateLessAssignments(allAssignments);
  return { assignments: regularAssignments, lessAssignments };
};

/**
 * Determines if the default assignments dialog should be shown
 * @param {boolean} isFeatureFlagActive - Whether the DisplayConfigurationState flag is active and visible
 * @param {number} defaultAssignmentsCount - Number of default assignments
 * @returns {boolean} True if dialog should be shown
 */
export const shouldShowDefaultAssignmentsDialog = (
  isFeatureFlagActive: boolean,
  defaultAssignmentsCount: number
): boolean => {
  return isFeatureFlagActive && defaultAssignmentsCount > 0;
};

/**
 * Checks whether a configuration variable has default-assigned value
 * @param {IConfigurationVariable} variable - The configuration variable to check
 * @returns {boolean} True if the variable has a default assignment, false otherwise
 */
export function hasDefaultValue(variable: IConfigurationVariable): boolean {
  return variable.values.some((v) => {
    const isMatch = v.assigned === EJustification.Default;

    if (
      variable.variableType === EVariableType.Number ||
      variable.variableType === EVariableType.Submodel
    ) {
      return isMatch && Number(v.value) > 0;
    } else if (variable.variableType === EVariableType.String) {
      return isMatch && String(v.value).trim() !== '';
    }

    return isMatch;
  });
}
